<template>
  <div>
    <h4 class="table-page-header">Audit Logs</h4>
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group search-group">
          <el-input
            v-model="searchQuery"
            placeholder="Search by email, details, path..."
            clearable
            @clear="clearSearch"
            @input="onSearch"
            class="search-input"
          >
            <i
              slot="prepend"
              class="fa fa-search"
              style="padding-left: 4px; padding-right: 4px"
            ></i>
          </el-input>
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="actionFilter"
            :options="actionOptions"
            placeholder="All Actions"
            @change="onFilterChange"
            class="compact-filter"
            clearable
          />
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="resourceFilter"
            :options="resourceOptions"
            placeholder="All Resources"
            @change="onFilterChange"
            class="compact-filter"
            clearable
          />
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="successFilter"
            :options="successOptions"
            placeholder="All Status"
            @change="onFilterChange"
            class="compact-filter"
            clearable
          />
        </div>
        <div class="filter-group">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="-"
            start-placeholder="Start date"
            end-placeholder="End date"
            @change="onFilterChange"
            size="small"
            :picker-options="{ firstDayOfWeek: 1 }"
          />
        </div>
      </div>
    </div>
    <AIVTable
      :columns="columns"
      :data="logs"
      :pagination="pagination"
      :item-name="'audit logs'"
      @pageChange="fetchLogs"
      @pageSizeChange="handlePageSizeChange"
      @sort-change="handleSortChange"
    >
      <template #cell-action="{ row }">
        <span :class="getActionBadgeClass(row.action)">
          {{ row.action }}
        </span>
      </template>
      <template #cell-success="{ row }">
        <i
          :class="row.success ? 'fa fa-check text-success' : 'fa fa-times text-danger'"
        ></i>
      </template>
      <template #cell-changed_fields="{ row }">
        <span v-if="row.changed_fields && row.changed_fields.length">
          {{ row.changed_fields.join(", ") }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>
      <template #cell-createdAt="{ row }">
        {{ formatDate(row.createdAt) }}
      </template>
      <template #cell-actions="{ row }">
        <button
          class="btn btn-sm btn-info"
          @click="viewDetail(row)"
          title="View Details"
        >
          <i class="fa fa-eye"></i>
        </button>
      </template>
    </AIVTable>

    <!-- Detail Modal -->
    <AIVDialog
      v-model="$modal.state.audit_detail"
      title="Audit Log Detail"
      width="70%"
      height="80vh"
    >
      <div v-if="selectedLog" class="audit-detail">
        <div class="detail-section">
          <h6>Summary</h6>
          <table class="detail-table">
            <tr>
              <td class="detail-label">ID</td>
              <td>{{ selectedLog.id }}</td>
            </tr>
            <tr>
              <td class="detail-label">Timestamp</td>
              <td>{{ formatDate(selectedLog.createdAt) }}</td>
            </tr>
            <tr>
              <td class="detail-label">User</td>
              <td>{{ selectedLog.user_name }}</td>
            </tr>
            <tr>
              <td class="detail-label">Action</td>
              <td>
                <span :class="getActionBadgeClass(selectedLog.action)">
                  {{ selectedLog.action }}
                </span>
              </td>
            </tr>
            <tr>
              <td class="detail-label">Resource</td>
              <td>{{ selectedLog.resource }}</td>
            </tr>
            <tr>
              <td class="detail-label">Resource ID</td>
              <td>{{ selectedLog.resource_id || "-" }}</td>
            </tr>
            <tr>
              <td class="detail-label">Method</td>
              <td>{{ selectedLog.method }}</td>
            </tr>
            <tr>
              <td class="detail-label">Path</td>
              <td>{{ selectedLog.path }}</td>
            </tr>
            <tr>
              <td class="detail-label">IP Address</td>
              <td>{{ selectedLog.ip_address }}</td>
            </tr>
            <tr>
              <td class="detail-label">Status</td>
              <td>
                <i :class="selectedLog.success ? 'fa fa-check text-success' : 'fa fa-times text-danger'"></i>
                {{ selectedLog.success ? "Success" : "Failure" }}
              </td>
            </tr>
            <tr v-if="selectedLog.details">
              <td class="detail-label">Details</td>
              <td>{{ selectedLog.details }}</td>
            </tr>
          </table>
        </div>

        <div
          v-if="selectedLog.changed_fields && selectedLog.changed_fields.length"
          class="detail-section"
        >
          <h6>Changes</h6>
          <table class="changes-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="field in selectedLog.changed_fields"
                :key="field"
              >
                <td class="field-name">{{ field }}</td>
                <td class="before-value">
                  {{ formatValue(selectedLog.before_data && selectedLog.before_data[field]) }}
                </td>
                <td class="after-value">
                  {{ formatValue(selectedLog.after_data && selectedLog.after_data[field]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-else-if="selectedLog.before_data || selectedLog.after_data"
          class="detail-section"
        >
          <h6>Data Snapshot</h6>
          <div v-if="selectedLog.before_data" class="snapshot-section">
            <strong>Before:</strong>
            <pre class="data-pre">{{ JSON.stringify(selectedLog.before_data, null, 2) }}</pre>
          </div>
          <div v-if="selectedLog.after_data" class="snapshot-section">
            <strong>After:</strong>
            <pre class="data-pre">{{ JSON.stringify(selectedLog.after_data, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </AIVDialog>
  </div>
</template>

<script>
import { auditLogsService } from "src/serviceHandlers/auditLogsService";

export default {
  name: "AuditLogs",
  data() {
    return {
      logs: [],
      selectedLog: null,
      searchQuery: "",
      actionFilter: "",
      resourceFilter: "",
      successFilter: "",
      dateRange: null,
      actionOptions: [],
      resourceOptions: [],
      successOptions: [
        { label: "Success", value: "true" },
        { label: "Failure", value: "false" },
      ],
      pagination: {
        total: 0,
        totalPages: 1,
        currentPage: 1,
        perPage: 25,
      },
      sortField: "createdAt",
      sortOrder: "DESC",
      columns: [
        { key: "createdAt", label: "Timestamp" },
        { key: "user_name", label: "User", sortable: false },
        { key: "action", label: "Action" },
        { key: "resource", label: "Resource" },
        { key: "resource_id", label: "ID", class: "hide-on-medium" },
        { key: "success", label: "Status", sortable: false },
        { key: "changed_fields", label: "Changed Fields", sortable: false, class: "hide-on-medium" },
        { key: "details", label: "Details", sortable: false, class: "hide-on-small" },
        { key: "actions", label: "", sortable: false },
      ],
      searchTimeout: null,
    };
  },
  async created() {
    await this.loadFilterOptions();
    await this.fetchLogs();
  },
  methods: {
    async loadFilterOptions() {
      try {
        const [actions, resources] = await Promise.all([
          auditLogsService.getActionTypes(),
          auditLogsService.getResourceTypes(),
        ]);
        this.actionOptions = actions.map((a) => ({ label: a, value: a }));
        this.resourceOptions = resources.map((r) => ({ label: r, value: r }));
      } catch (err) {
        // silently handle error
      }
    },
    async fetchLogs(page) {
      try {
        this.$spinner.show();
        const params = {
          page: page || this.pagination.currentPage,
          perPage: this.pagination.perPage,
          sortField: this.sortField,
          sortOrder: this.sortOrder,
        };

        if (this.searchQuery) params.searchTerm = this.searchQuery;
        if (this.actionFilter) params.action = this.actionFilter;
        if (this.resourceFilter) params.resource = this.resourceFilter;
        if (this.successFilter !== "" && this.successFilter !== null) params.success = this.successFilter;
        if (this.dateRange && this.dateRange.length === 2) {
          params.startDate = this.dateRange[0].toISOString().split("T")[0];
          params.endDate = this.dateRange[1].toISOString().split("T")[0];
        }

        const result = await auditLogsService.list(params);
        this.logs = result.rows;
        this.pagination = result.pagination;
      } catch (err) {
        this.$notifyError("Failed to load audit logs");
      } finally {
        this.$spinner.hide();
      }
    },
    async viewDetail(row) {
      try {
        this.selectedLog = await auditLogsService.getById(row.id);
        this.$modal.open("audit_detail");
      } catch (err) {
        this.$notifyError("Failed to load audit log details");
      }
    },
    onSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.currentPage = 1;
        this.fetchLogs(1);
      }, 400);
    },
    clearSearch() {
      this.searchQuery = "";
      this.pagination.currentPage = 1;
      this.fetchLogs(1);
    },
    onFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchLogs(1);
    },
    handlePageSizeChange(size) {
      this.pagination.perPage = size;
      this.pagination.currentPage = 1;
      this.fetchLogs(1);
    },
    handleSortChange({ prop, order }) {
      this.sortField = prop || "createdAt";
      this.sortOrder = order === "ascending" ? "ASC" : "DESC";
      this.fetchLogs(this.pagination.currentPage);
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      const d = new Date(dateStr);
      return d.toLocaleString("en-US", {
        timeZone: "America/Denver",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    formatValue(val) {
      if (val === null || val === undefined) return "-";
      if (typeof val === "object") return JSON.stringify(val);
      return String(val);
    },
    getActionBadgeClass(action) {
      if (!action) return "action-badge";
      if (action.includes("CREATE") || action.includes("SUCCESS")) return "action-badge badge-create";
      if (action.includes("UPDATE") || action.includes("MODIFY")) return "action-badge badge-update";
      if (action.includes("DELETE")) return "action-badge badge-delete";
      if (action.includes("FAILURE") || action.includes("ERROR") || action.includes("VIOLATION")) return "action-badge badge-error";
      if (action.includes("READ") || action.includes("ACCESS")) return "action-badge badge-read";
      return "action-badge";
    },
  },
};
</script>

<style scoped>
.filter-section {
  margin-bottom: 16px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.filter-group {
  flex-shrink: 0;
}

.search-group {
  flex: 1;
  min-width: 200px;
  max-width: 350px;
}

.compact-filter {
  min-width: 140px;
}

.action-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background-color: #6c757d;
}

.badge-create {
  background-color: #28a745;
}

.badge-update {
  background-color: #6ba6cd;
}

.badge-delete {
  background-color: #dc3545;
}

.badge-error {
  background-color: #fd7e14;
}

.badge-read {
  background-color: #6c757d;
}

.audit-detail {
  padding: 10px;
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section h6 {
  color: #6ba6cd;
  border-bottom: 1px solid #444;
  padding-bottom: 6px;
  margin-bottom: 12px;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
}

.detail-table td {
  padding: 6px 12px;
  border-bottom: 1px solid #333;
}

.detail-label {
  font-weight: 600;
  color: #aaa;
  width: 140px;
  white-space: nowrap;
}

.changes-table {
  width: 100%;
  border-collapse: collapse;
}

.changes-table th {
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid #444;
  color: #aaa;
  font-size: 12px;
  text-transform: uppercase;
}

.changes-table td {
  padding: 6px 12px;
  border-bottom: 1px solid #333;
  word-break: break-word;
  max-width: 300px;
}

.field-name {
  font-weight: 600;
  color: #6ba6cd;
}

.before-value {
  color: #dc3545;
}

.after-value {
  color: #28a745;
}

.snapshot-section {
  margin-bottom: 12px;
}

.data-pre {
  background: #1a1a2e;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 10px;
  font-size: 12px;
  max-height: 300px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.text-muted {
  color: #666;
}
</style>
